// components/UI/DialogModal/MsgModal.jsx

import React, { useEffect, useId, useRef, useState } from "react";

import {
	Box,
	Button,
	MetreaAlert,
	MetreaCheckbox,
	MetreaDialogBackdrop,
	MetreaDialogBody,
	MetreaDialogContent,
	MetreaDialogFooter,
	MetreaDialogHeader,
	MetreaDialogRoot,
	MetreaDialogTitle,
	Stack,
	Text,
} from "@metrea-ui/v2";

const MsgModal = (props) => {
	const {
		title = "Message",
		message = "",
		type = "info",
		callback,
		onConfirm,
		cancelCallback,
		onCancel,
		retryCallback,
		confirmLabel = "Yes, Continue", // optional override
		cancelLabel = "Cancel", // optional override
		okLabel = "Okay", // for non-confirm dialogs
		retryLabel = "Retry",
		closeLabel = "Close",
		// optional checkbox support
		checkboxLabel,
		checkboxDefault = false,
	} = props;

	const cancelRan = useRef(false);
	const titleId = useId();
	const descriptionId = useId();

	const typeLower = type.toLowerCase();
	const arrayMsg = Array.isArray(message) ? message : [message];
	const resolvedConfirm = typeof callback === "function" ? callback : onConfirm;
	const resolvedCancel = typeof cancelCallback === "function" ? cancelCallback : onCancel;

	//  local state for the checkbox
	const [checked, setChecked] = useState(checkboxDefault);

	useEffect(() => {
		cancelRan.current = false;
		// re-sync default when a new dialog opens
		setChecked(checkboxDefault);
	}, [checkboxDefault, title, message, type]);

	const handleConfirm = () => {
		// pass checkbox value back to the caller
		if (typeof resolvedConfirm === "function") resolvedConfirm(checked);
	};

	const handleCancel = () => {
		if (cancelRan.current) return;
		cancelRan.current = true;

		// also pass checkbox value on cancel
		if (typeof resolvedCancel === "function") resolvedCancel(checked);
	};

	const handleRetry = () => {
		if (typeof retryCallback === "function") retryCallback();
	};

	const handleOpenChange = (next) => {
		const isNowOpen = typeof next === "boolean" ? next : next?.open ?? next?.target?.open ?? false;

		if (!isNowOpen) {
			handleCancel();
		}
	};

	const renderMessageLines = () => (
		<Stack gap="2" id={descriptionId}>
			{arrayMsg.map((msg, index) => (
				<Box key={index} minH="0.5rem">
					<Text whiteSpace="pre-wrap">{typeof msg === "string" && msg.trim() === "" ? "\u00A0" : msg}</Text>
				</Box>
			))}
		</Stack>
	);

	return (
		<MetreaDialogRoot
			open
			onOpenChange={handleOpenChange}
			skipAnimationOnMount
			placement="center"
			motionPreset="none"
		>
			<MetreaDialogBackdrop
				data-testid="msg-modal-backdrop"
				onClick={handleCancel}
				style={{ background: "rgba(0, 0, 0, 0.75)" }}
			/>
			<MetreaDialogContent
				backdrop={false}
				data-testid="msg-modal-content"
				aria-labelledby={titleId}
				aria-describedby={descriptionId}
				style={{
					backgroundColor: "white",
					width: "min(40rem, calc(100vw - 2rem))",
					maxWidth: "40rem",
					color: "inherit",
					overflow: "hidden",
				}}
			>
				<MetreaDialogHeader
					style={{
						background: typeLower === "error" ? "var(--metrea-Danger)" : "var(--metrea-StonyBlue)",
						padding: "1rem",
					}}
				>
					<MetreaDialogTitle id={titleId} style={{ color: "white", margin: 0 }}>
						{title}
					</MetreaDialogTitle>
				</MetreaDialogHeader>
				<MetreaDialogBody style={{ padding: "1rem" }}>
					{typeLower === "error" ? (
						<MetreaAlert status="error" variant="subtle">
							<Box width="100%">{renderMessageLines()}</Box>
						</MetreaAlert>
					) : (
						renderMessageLines()
					)}

					{/* Checkbox row (only renders when label provided). Keep it controlled so
					    confirm callback returns the real user selection. */}
					{checkboxLabel && (
						<MetreaCheckbox
							mt={6}
							checked={checked}
							onCheckedChange={(nextChecked) => {
								const nextValue =
									nextChecked === true ||
									nextChecked === "indeterminate" ||
									nextChecked?.checked === true ||
									nextChecked?.detail?.checked === true;
								setChecked(nextValue);
							}}
						>
							{checkboxLabel}
						</MetreaCheckbox>
					)}
				</MetreaDialogBody>
				<MetreaDialogFooter style={{ padding: "1rem", display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
					{typeLower === "confirm" && (
						<>
							<Button color="secondary" onClick={handleCancel}>
								{cancelLabel}
							</Button>
							<Button color="primary" onClick={handleConfirm}>
								{confirmLabel}
							</Button>
						</>
					)}

					{typeLower === "error" && (
						<>
							{retryCallback && (
								<Button color="primary" onClick={handleRetry}>
									{retryLabel}
								</Button>
							)}
							<Button color="error" onClick={handleCancel}>
								{closeLabel}
							</Button>
						</>
					)}

					{!["confirm", "error"].includes(typeLower) && (
							<Button color="primary" onClick={handleConfirm}>
								{okLabel}
							</Button>
						)}
				</MetreaDialogFooter>
			</MetreaDialogContent>
		</MetreaDialogRoot>
	);
};

export default MsgModal;
